import { isBrowser } from "../../services/general";
import { parse, stringify } from "flatted";

/**
 * getContextFromSession
 * A function to get the context from session
 *
 * @param {string} [expectedFlow=null] - A string that indicates the module name to check in session storage
 * @return {(object|null)} Return an object containing the data of the specified module in the state unless ```expectedFlow``` is not specified
 **/

export const getContextFromSession = (expectedFlow = null) => {
  // Get data from session storage if contextState item exists
  const data =
    isBrowser() && sessionStorage.getItem("contextState")
      ? parse(sessionStorage.getItem("contextState"))
      : {};

  // If user doesn't pass expected flow, return null
  if (expectedFlow == null || expectedFlow?.trim() === "") return null;

  // If one of the keys in the session storage
  // match the expected flow, return the value of
  // that key
  if (Object.keys(data)?.includes(expectedFlow?.trim()?.toLowerCase()))
    return data?.[expectedFlow];
};

/**
 * saveContextToSession
 * A function to save the context into the session, based on the passed flow if specified
 *
 * @param {object} [state] - An object containing the data to save in session storage
 * @param {string} [flow=null] - A string that indicates the module name to set in session storage
 * @return {null} Returns null
 **/

export const saveContextToSession = (state, flow = null) => {
  flow = flow?.trim()?.toLowerCase();
  if (!isBrowser()) return null;
  // Check if there's already data in the session storage
  const data =
    isBrowser() && sessionStorage.getItem("contextState")
      ? parse(sessionStorage.getItem("contextState"))
      : {};
  let payload;
  // If session storage is empty, set it based on the values of the state
  // else append the values of the state into the values of the current session storage
  if (!data) {
    payload = flow ? { [flow]: { ...state } } : { ...state };
  } else {
    payload = flow ? { ...data, [flow]: { ...state } } : { ...data, ...state };
  }
  sessionStorage.setItem("contextState", stringify(payload));
  return null;
};

/**
 * readLatestFlowStateFromSession
 * Reads the freshest flow state from session storage at submit time.
 * Session values take precedence over the in-memory fallback state.
 *
 * @param {string} flowId
 * @param {object} [fallbackState={}]
 * @return {object}
 */
export const readLatestFlowStateFromSession = (flowId, fallbackState = {}) => {
  const sessionState = getContextFromSession(flowId);

  if (!sessionState) return fallbackState;

  return { ...fallbackState, ...sessionState };
};

/**
 * removeContextFromSession
 * A function to remove data from the session, based on the parameters passed
 *
 * @param {string} [expectedFlow=null] - A string that indicates the module name to check in session storage
 * @return {null} Returns null
 **/

export const removeContextFromSession = (expectedFlow = null) => {
  if (!isBrowser()) return null;

  // Remove the whole context from the session if expectedFlow
  // is empty
  if (expectedFlow == null || expectedFlow?.trim() === "")
    sessionStorage.removeItem("contextState");
  // Remove only the specific flow from the session if expectedFlow
  // has a value and it exists as a key in the session object
  else if ((expectedFlow != null) & (expectedFlow?.trim() !== "")) {
    const data =
      isBrowser() && sessionStorage.getItem("contextState")
        ? parse(sessionStorage.getItem("contextState"))
        : {};
    const flowKey = expectedFlow?.trim()?.toLowerCase();
    if (Object.keys(data)?.includes(flowKey)) {
      delete data[flowKey];
      sessionStorage.setItem("contextState", stringify(data));
    }
  }
  return null;
};
